import React, { useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { Link } from 'react-router-dom';
import { Brain, ArrowLeft } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export default function ForgotPasswordPage() {
  useDocumentTitle('Reset Password');
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccessMessage('');
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
      setEmail('');
    } catch (err) {
      setError('Failed to send reset email. Please check your email address.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-light to-background-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center text-text-secondary hover:text-primary-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to login
        </Link>

        <div className="bg-surface-light rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Brain className="w-8 h-8 text-primary-500" />
            <h1 className="text-2xl font-bold text-text-primary">CampusConnect</h1>
          </div>

          <h2 className="text-2xl font-semibold text-text-primary text-center mb-6">
            Reset your password
          </h2>

          <p className="text-text-secondary text-center mb-8">
            Enter your email address and we'll send you instructions to reset your password.
          </p>

          {error && (
            <div className="bg-error-light/20 text-error-dark px-4 py-2 rounded-lg mb-6">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="bg-success-light/20 text-success-dark px-4 py-2 rounded-lg mb-6">
              {successMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="you@school.edu"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 
                       rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending reset email...' : 'Send reset instructions'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}